import { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid, KeenIcon, Modal } from '@/components';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import usePagination from '@/hooks/usePagination';
import { ErrorApiResponse } from '@/types/api.types';
import { ConfirmationDialog } from '@/components/dialogBox';
import {
  BerthListQueryParams,
  BerthStatus,
  IBerthData,
  MediaPayload,
  PriceUnit,
} from '@/types/berth-management.types';
import {
  useDeleteBerthMutation,
  useGetBerthsQuery,
  useUpdateBerthMutation,
  useGetBerthByIdQuery,
  useUpdateFeaturedBerthMutation,
  useCreateMediaBerthIdMutation,
  useCreateBerthGroupMutation,
  useCreateBerthMutation,
  useBlockUnavailabilityBerthMutation,
  useDownloadBerthQrCodeMutation,
} from '@/services/BerthManagementService';
import { BerthModal } from './BerthModal';
import { useGetHarborsQuery } from '@/services/HarborManagementService';
import Select from 'react-select';
import { HarborOption } from '../booking-management/BookingListings';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { berthValidationSchema } from '@/utils/validations';
import { usePermissions } from '@/hooks/usePermissions';
import { BerthType } from '@/constant';
import { formatFinnishCurrency } from '@/utils';

// Interface for unavailability data
interface UnavailabilityData {
  start_date?: string;
  end_date?: string;
}

// Utility function to compare arrays
const arraysEqual = (
  arr1: string[] | undefined | null,
  arr2: string[] | undefined | null
) => {
  const a1 = arr1 ?? [];
  const a2 = arr2 ?? [];
  if (a1.length !== a2.length) return false;
  const sortedA1 = [...a1].sort();
  const sortedA2 = [...a2].sort();
  return sortedA1.every((item, index) => item === sortedA2[index]);
};

const getBerthTypeLabel = (value?: number) => {
  if (value === BerthType.TRANSIENT) return 'Transient';
  if (value === BerthType.SEASONAL) return 'Seasonal';
  if (value === BerthType.YEARLY) return 'Yearly';
  return '-';
};

const BerthListings = () => {
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    berth: IBerthData | null;
    mode: 'view' | 'edit' | 'create';
  }>({ isOpen: false, berth: null, mode: 'view' });
  const [confirmDeleteState, setConfirmDeleteState] = useState<{
    isOpen: boolean;
    berthId: string | null;
  }>({ isOpen: false, berthId: null });
  const [confirmFeaturedState, setConfirmFeaturedState] = useState<{
    isOpen: boolean;
    berthId: string | null;
    featured: boolean;
  }>({ isOpen: false, berthId: null, featured: false });
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupNameError, setGroupNameError] = useState('');
  const [selectedBerthIds, setSelectedBerthIds] = useState<string[]>([]);
  const [featuredFilter, setFeaturedFilter] = useState<string>('all');
  const [formData, setFormData] = useState<Partial<IBerthData>>({
    berth_id: '',
    size_category: '',
    length: undefined,
    width: undefined,
    depth: undefined,
    depth_unit: 'm',
    amenities: [],
    grouping: '',
    status: 1,
    media: [],
    harbor: { id: '', name: '', location: '' },
    harbor_id: '',
    id: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const [
    updateBerth,
    { isLoading: isUpdating, isSuccess: updateSuccess, isError: updateError },
  ] = useUpdateBerthMutation();
  const [deleteBerth] = useDeleteBerthMutation();
  const [updateFeaturedBerth] = useUpdateFeaturedBerthMutation();
  const [downloadBerthQrCode] = useDownloadBerthQrCodeMutation();
  const [downloadingQrId, setDownloadingQrId] = useState<string | null>(null);

  const handleDownloadQrCode = useCallback(
    async (berth: IBerthData) => {
      try {
        setDownloadingQrId(berth.id as string);
        const blob = await downloadBerthQrCode(berth.id as string).unwrap();
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `berth-${berth.berth_number}-qr.png`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
      } catch {
        enqueueSnackbar('Failed to download QR code', { variant: 'error' });
      } finally {
        setDownloadingQrId(null);
      }
    },
    [downloadBerthQrCode, enqueueSnackbar]
  );
  const [
    createBerthGroup,
    {
      isLoading: isCreatingGroup,
      isSuccess: createGroupSuccess,
      isError: createGroupError,
    },
  ] = useCreateBerthGroupMutation();
  const [createMediaBerthId] = useCreateMediaBerthIdMutation();
  const [createBerth] = useCreateBerthMutation();
  const [blockUnavailabilityBerth] = useBlockUnavailabilityBerthMutation();
  const [berthIdToFetch, setBerthIdToFetch] = useState<string | null>(null);
  const [harborOptions, setHarborOptions] = useState<HarborOption[]>([]);
  const harborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );
  const isHarborDetailsRoute = location.pathname === '/harbors/details';

  // Initialize selectedHarbor with harborId from Redux store
  const [selectedHarbor, setSelectedHarbor] = useState<string>(
    harborId && location.pathname === '/harbors/details' ? harborId : ''
  );

  const queryParams: BerthListQueryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    harbor_id: selectedHarbor || undefined,
  };

  if (featuredFilter !== 'all') {
    queryParams.is_featured = featuredFilter === 'featured';
  }

  const {
    data: berthsResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetBerthsQuery(queryParams);

  const {
    data: berthResponse,
    isSuccess: berthFetchSuccess,
    error: berthFetchError,
    isFetching: berthFetching,
  } = useGetBerthByIdQuery(berthIdToFetch!, {
    skip: !berthIdToFetch,
  });

  const [harborPagination, setHarborPagination] = useState({
    page: 1,
    limit: 10,
  });

  const { data: harborsData, isLoading: isHarborsLoading } = useGetHarborsQuery(
    {}
  );

  useEffect(() => {
    if (harborsData?.data) {
      const options = harborsData.data.map((harbor) => ({
        value: harbor.id,
        label: harbor.name,
      }));
      setHarborOptions(options);
    }
  }, [harborsData]);

  useEffect(() => {
    if (typeof refetch === 'function') {
      refetch();
    }
  }, [debouncedValue, selectedHarbor, refetch]);

  const handleHarborChange = (selectedOption: HarborOption | null) => {
    const harborId = selectedOption ? selectedOption.value : '';
    setSelectedHarbor(harborId);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  useEffect(() => {
    if (berthsResponse) {
      handleTotalPages(
        berthsResponse.totalPages || berthsResponse.total_pages || 1
      );
      handleTotalItems(
        berthsResponse.totalItems ||
          berthsResponse.total ||
          berthsResponse.total_items ||
          0
      );
    }
  }, [berthsResponse, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    if (berthFetchSuccess && berthResponse) {
      const formatDateForInput = (dateString?: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const normalizeSeasonalPricing = (
        seasonalPricing: unknown
      ): Partial<Record<string, number>> | undefined => {
        if (!seasonalPricing || typeof seasonalPricing !== 'object')
          return undefined;
        return seasonalPricing as Partial<Record<string, number>>;
      };

      const berthData: IBerthData = {
        id: berthResponse.data.id,
        berth_number: berthResponse.data.berth_number,
        category_id: berthResponse.data.category_id,
        berth_type: berthResponse.data.berth_type,
        amenities: berthResponse.data.amenities ?? [],
        price: berthResponse.data.price,
        price_unit: berthResponse.data.price_unit,
        seasonal_pricing: normalizeSeasonalPricing(
          (berthResponse.data as unknown as { seasonal_pricing?: unknown })
            ?.seasonal_pricing
        ),
        status: berthResponse.data.status,
        created_at: berthResponse.data.created_at,
        featured: berthResponse.data.featured ?? false,
        harbor: {
          id: berthResponse.data.harbor?.id ?? '',
          name: berthResponse.data.harbor?.name ?? '',
          location: berthResponse.data.harbor?.location ?? '',
        },
        harbor_id: berthResponse.data.harbor?.id ?? '',
        pier_id: berthResponse.data.pier?.id ?? '',
        pier:
          berthResponse.data.pier?.id && berthResponse.data.pier?.name
            ? ({
                id: berthResponse.data.pier.id,
                name: berthResponse.data.pier.name,
              } as { id: string; name: string } | null | undefined)
            : null,
        mooring_style: berthResponse.data.mooring_style,
        start_date: formatDateForInput(
          berthResponse.data.unavailabilities?.[0]?.start_date
        ),
        end_date: formatDateForInput(
          berthResponse.data.unavailabilities?.[0]?.end_date
        ),
      };

      setFormData(berthData);
      setModalState((prev) => ({
        ...prev,
        isOpen: true,
        berth: berthData,
        mode: prev.mode,
      }));
      setErrors({});
      setBerthIdToFetch(null);
    }
  }, [berthFetchSuccess, berthResponse]);

  useEffect(() => {
    if (berthFetchError) {
      enqueueSnackbar('Failed to fetch berth details', { variant: 'error' });
      setBerthIdToFetch(null);
    }
  }, [berthFetchError, enqueueSnackbar]);

  useEffect(() => {
    if (updateSuccess) {
      enqueueSnackbar('Berth updated successfully', { variant: 'success' });
      handleCloseModal();
      refetch();
    }
  }, [updateSuccess, enqueueSnackbar, refetch]);

  useEffect(() => {
    if (updateError) {
      enqueueSnackbar('Failed to update berth', { variant: 'error' });
    }
  }, [updateError, enqueueSnackbar]);

  useEffect(() => {
    if (createGroupSuccess) {
      enqueueSnackbar('Berth group created successfully', {
        variant: 'success',
      });
      setGroupModalOpen(false);
      setGroupName('');
      setSelectedBerthIds([]);
      refetch();
    }
  }, [createGroupSuccess, enqueueSnackbar, refetch]);

  useEffect(() => {
    if (createGroupError) {
      const errorMessage =
        (createGroupError as unknown as ErrorApiResponse).data?.message ||
        'Failed to create group';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  }, [createGroupError, enqueueSnackbar]);

  const handleToggleFeatured = async (id: string, featured: boolean) => {
    try {
      const payload = {
        harborId: id,
        featured: !featured,
      };
      await updateFeaturedBerth(payload);
      enqueueSnackbar(
        `Berth ${!featured ? 'marked as featured' : 'removed from featured'}`,
        { variant: 'success' }
      );
      refetch();
    } catch (error) {
      enqueueSnackbar(
        (error as ErrorApiResponse)?.message ||
          'Failed to update featured status',
        { variant: 'error' }
      );
    }
  };

  const filteredData: IBerthData[] = useMemo(() => {
    if (!berthsResponse?.data) return [];

    const filteredBerths =
      berthsResponse.data.filter((berth) => {
        if (!berth) return false;
        if (featuredFilter === 'all') return true;
        return (
          (berth.status === undefined || berth.status === 1) &&
          berth.featured === (featuredFilter === 'featured')
        );
      }) || [];

    return filteredBerths.map((berth) => ({
      id: berth.id || '',
      berth_number: berth.berth_number || '',
      harbor: berth.harbor || { id: '', name: '' },
      berth_type: berth.berth_type,
      category_id: berth.category_id || '',
      price: berth.price,
      amenities: berth.amenities || [],
      status: berth.status,
      created_at: berth.created_at || '',
      featured: berth.featured ?? false,
      harbor_id: berth.harbor?.id || '',
      pier:
        berth.pier?.id && berth.pier?.name
          ? { id: berth.pier.id, name: berth.pier.name }
          : null,
      unavailabilities: berth.unavailabilities,
    }));
  }, [berthsResponse, featuredFilter]);

  const resetFilters = () => {
    if (searchTerm) resetSearch();
    setFeaturedFilter('all');
    setSelectedBerthIds([]);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setSelectedHarbor(harborId && isHarborDetailsRoute ? harborId : '');
  };

  const handleOpenModal = useCallback(
    (berth: IBerthData | null, mode: 'view' | 'edit' | 'create') => {
      if (mode === 'create') {
        const selectedHarborOption = harborOptions.find(
          (option) => option.value === selectedHarbor
        );
        setModalState({
          isOpen: true,
          berth: null,
          mode: 'create',
        });
        setFormData({
          category_id: '',
          amenities: [],
          price: undefined,
          status: BerthStatus.AVAILABLE,
          harbor: {
            id: selectedHarbor || '',
            name: selectedHarborOption ? selectedHarborOption.label : '',
            location: '',
          },
          harbor_id: selectedHarbor || '',
          berth_number: '',
          featured: false,
        });
        setErrors({});
      } else if (berth?.id && mode === 'view') {
        setBerthIdToFetch(berth.id);
        setModalState({
          isOpen: true,
          berth: berth,
          mode: 'view',
        });
      } else if (berth?.id && mode === 'edit') {
        setBerthIdToFetch(berth.id);
        setModalState({
          isOpen: true,
          berth: berth,
          mode: 'edit',
        });
      }
    },
    [
      harborOptions,
      selectedHarbor,
      setModalState,
      setFormData,
      setErrors,
      setBerthIdToFetch,
    ]
  );

  const handleCloseModal = () => {
    setModalState({ isOpen: false, berth: null, mode: 'view' });
    setFormData({});
    setErrors({});
    setBerthIdToFetch(null);
  };

  const handleSubmit = async (unavailabilityData?: UnavailabilityData) => {
    if (!formData) return;
    try {
      await berthValidationSchema.validate(formData, { abortEarly: false });

      const normalizeSeasonalPricing = (
        seasonalPricing: unknown
      ): Partial<Record<string, number>> | undefined => {
        if (!seasonalPricing || typeof seasonalPricing !== 'object')
          return undefined;
        return seasonalPricing as Partial<Record<string, number>>;
      };

      if (modalState.mode === 'create') {
        const createPayload = {
          harbor_id: formData.harbor_id || '',
          category_id: formData.category_id || '',
          berth_type:
            formData.berth_type === undefined || formData.berth_type === null
              ? undefined
              : Number(formData.berth_type),
          amenities: formData.amenities || [],
          ...(Number(formData.berth_type) === BerthType.SEASONAL
            ? {
                seasonal_pricing: normalizeSeasonalPricing(
                  (formData as unknown as { seasonal_pricing?: unknown })
                    ?.seasonal_pricing
                ),
              }
            : {
                price: Number(formData.price),
                price_unit: formData.price_unit as PriceUnit,
              }),
          status: Number(formData.status) || BerthStatus.AVAILABLE,
          count: Number(formData.quantity) || 1,
          pier_id: formData.pier_id || '',
          pier_prefix: formData.pier_prefix || '',
          mooring_style: formData.mooring_style || '',
        };

        const createResponse = await createBerth(createPayload).unwrap();

        if (Number(formData.status) === BerthStatus.NOT_AVAILABLE) {
          const unavailabilityPayload: {
            id: string;
            startDate?: string;
            endDate?: string;
            reason: string;
          } = {
            id: createResponse?.id,
            reason: '',
          };

          // Only include startDate and endDate if they are provided
          if (unavailabilityData?.start_date) {
            unavailabilityPayload.startDate = unavailabilityData.start_date;
          }
          if (unavailabilityData?.end_date) {
            unavailabilityPayload.endDate = unavailabilityData.end_date;
          }

          await blockUnavailabilityBerth(unavailabilityPayload).unwrap();
          enqueueSnackbar(
            'Berth(s) created and unavailability set successfully',
            {
              variant: 'success',
            }
          );
        } else {
          enqueueSnackbar('Berth created successfully', { variant: 'success' });
        }

        handleCloseModal();
      } else if (modalState.mode === 'edit' && modalState.berth) {
        const originalData = modalState.berth;
        const updatePayload = {
          berth_number: formData.berth_number ?? '',
          category_id: formData.category_id ?? '',
          berth_type:
            formData.berth_type === undefined || formData.berth_type === null
              ? undefined
              : Number(formData.berth_type),
          amenities: formData.amenities ?? [],
          ...(Number(formData.berth_type) === BerthType.SEASONAL
            ? {
                seasonal_pricing: normalizeSeasonalPricing(
                  (formData as unknown as { seasonal_pricing?: unknown })
                    ?.seasonal_pricing
                ),
              }
            : {
                price: Number(formData.price),
              }),
          status: Number(formData.status) || BerthStatus.AVAILABLE,
          harbor_id: formData.harbor_id ?? '',
          pier_id: formData.pier_id ?? '',
          mooring_style: formData.mooring_style ?? '',
        };

        const hasChanges =
          updatePayload.berth_number !== originalData.berth_number ||
          updatePayload.category_id !== originalData.category_id ||
          updatePayload.berth_type !== originalData.berth_type ||
          !arraysEqual(updatePayload.amenities, originalData.amenities) ||
          ('price' in updatePayload &&
            updatePayload.price !== originalData.price) ||
          ('seasonal_pricing' in updatePayload &&
            JSON.stringify(updatePayload.seasonal_pricing ?? {}) !==
              JSON.stringify(
                (originalData as unknown as { seasonal_pricing?: unknown })
                  ?.seasonal_pricing ?? {}
              )) ||
          updatePayload.status !== originalData.status ||
          updatePayload.harbor_id !== originalData.harbor_id ||
          updatePayload.pier_id !== originalData.pier_id ||
          updatePayload.mooring_style !== originalData.mooring_style;

        let hasMediaChanges = false;
        const mediaArray: (File | string)[] = [...(formData.media ?? [])];
        const validMediaFiles = mediaArray.filter(
          (item: unknown): item is File => item instanceof File
        );

        if (validMediaFiles.length > 0) {
          const mediaPayload: MediaPayload = {
            id: modalState.berth.id,
            media: validMediaFiles,
          };
          await createMediaBerthId(mediaPayload).unwrap();
          hasMediaChanges = true;
          enqueueSnackbar('Media uploaded successfully', {
            variant: 'success',
          });
        }

        let hasUnavailabilityChanges = false;
        if (Number(formData.status) === BerthStatus.NOT_AVAILABLE) {
          const unavailabilityPayload: {
            id: string;
            startDate?: string;
            endDate?: string;
            reason: string;
          } = {
            id: modalState.berth.id,
            reason: '',
          };

          // Only include startDate and endDate if they are provided
          if (unavailabilityData?.start_date) {
            unavailabilityPayload.startDate = unavailabilityData.start_date;
          }
          if (unavailabilityData?.end_date) {
            unavailabilityPayload.endDate = unavailabilityData.end_date;
          }

          const unavailabilityResponse = await blockUnavailabilityBerth(
            unavailabilityPayload
          ).unwrap();

          if (unavailabilityResponse.conflictingBookings) {
            enqueueSnackbar(
              'Warning: There are existing bookings for this berth during the selected unavailability period. Unavailability has still been set.',
              { variant: 'warning' }
            );
          } else {
            enqueueSnackbar('Unavailability set successfully', {
              variant: 'success',
            });
          }

          hasUnavailabilityChanges = true;
        }

        if (hasChanges || hasMediaChanges || hasUnavailabilityChanges) {
          if (hasChanges) {
            await updateBerth({
              berth_id: modalState.berth.id as string,
              data: updatePayload,
            }).unwrap();
          }
        } else {
          enqueueSnackbar('No changes to update');
        }

        handleCloseModal();
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar(
          `Please fix the form errors (${err.inner.length} errors occurred)`,
          { variant: 'error' }
        );
      } else {
        const errorMessage =
          (err as ErrorApiResponse).data?.message || 'An error occurred';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    } finally {
      setBerthIdToFetch(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteState.berthId) return;
    try {
      await deleteBerth(confirmDeleteState.berthId).unwrap();
      enqueueSnackbar('Berth deleted successfully', { variant: 'success' });
      setConfirmDeleteState({ isOpen: false, berthId: null });
      refetch();
    } catch (err) {
      const errorMessage =
        (err as ErrorApiResponse).data?.message || 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setConfirmDeleteState({ isOpen: false, berthId: null });
    }
  };

  const handleCreateGroup = async () => {
    try {
      const groupNameSchema = Yup.string()
        .required('Group name is required')
        .min(3, 'Group name must be at least 3 characters');
      await groupNameSchema.validate(groupName);
      setGroupNameError('');

      await createBerthGroup({
        name: groupName,
        harbor_id: selectedHarbor || '',
      }).unwrap();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        setGroupNameError(err.message);
      } else {
        const errorMessage =
          (err as ErrorApiResponse).data?.message || 'Failed to create group';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const handleOpenCreateModal = () => {
    const selectedHarborOption = harborOptions.find(
      (option) => option.value === selectedHarbor
    );
    setModalState({
      isOpen: true,
      berth: null,
      mode: 'create',
    });
    setFormData({
      category_id: '',
      amenities: [],
      price: undefined,
      status: BerthStatus.AVAILABLE,
      harbor: {
        id: selectedHarbor || '',
        name: selectedHarborOption ? selectedHarborOption.label : '',
        location: '',
      },
      harbor_id: selectedHarbor || '',
      berth_number: '',
      featured: false,
    });
    setErrors({});
  };

  const columns = useMemo(
    () => [
      {
        accessor: (row: IBerthData) => row.harbor?.name || 'N/A',
        id: 'harbor_name',
        header: 'Harbor',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBerthData } }) => (
          <span>{row.original.harbor?.name || 'N/A'}</span>
        ),
      },
      {
        id: 'berth_type',
        header: 'Berth Type',
        accessor: (row: IBerthData) => row.berth_type,
        enableSorting: false,
        cell: ({ row }: { row: { original: IBerthData } }) => (
          <span>{getBerthTypeLabel(row.original.berth_type)}</span>
        ),
      },
      {
        accessor: (row: IBerthData) => row.berth_number,
        id: 'berth_number',
        header: 'Berth Number',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBerthData } }) =>
          hasPermission('berth_management_read') ? (
            <button
              onClick={() => handleOpenModal(row.original, 'view')}
              className="text-sm font-medium text-gray-900 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={`View ${row.original.berth_number}`}
            >
              {row.original.berth_number}
            </button>
          ) : (
            <span className="text-sm font-medium text-gray-900">
              {row.original.berth_number}
            </span>
          ),
      },
      {
        id: 'pier_name',
        header: 'Pier Name',
        cell: (info: { row: { original: IBerthData } }) => (
          <div
            className="truncate text-sm font-medium text-gray-900 hover:text-primary-active focus:outline-none focus:ring-2 focus:ring-blue-500"
            title={info.row.original.pier?.name || '-'}
          >
            {info.row.original.pier?.name || '-'}
          </div>
        ),
      },
      {
        accessor: (row: IBerthData) => row.price,
        id: 'price',
        header: 'Price',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBerthData } }) => {
          const price = row.original.price;

          if (price === undefined || price === null) {
            return <span>-</span>;
          }

          return (
            <span>{formatFinnishCurrency(price, { showCurrency: false })}</span>
          );
        },
      },
      {
        accessor: (row: IBerthData) => row.amenities,
        id: 'amenities',
        header: 'Amenities',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBerthData } }) => (
          <span>{(row.original.amenities || []).join(', ')}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        cell: (info: { row: { original: IBerthData } }) => {
          const status = info.row.original.status;
          return (
            <div className="flex flex-col gap-1">
              <span
                className={`badge ${
                  status === BerthStatus.AVAILABLE
                    ? 'badge-success'
                    : status === BerthStatus.NOT_AVAILABLE
                      ? 'badge-error'
                      : status === BerthStatus.OCCUPIED
                        ? 'badge-warning'
                        : 'badge-info'
                } badge-sm text-gray-900`}
              >
                {status === BerthStatus.AVAILABLE
                  ? 'Available'
                  : status === BerthStatus.NOT_AVAILABLE
                    ? 'Not Available'
                    : status === BerthStatus.OCCUPIED
                      ? 'Occupied'
                      : 'Maintenance'}
              </span>
            </div>
          );
        },
        size: 120,
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBerthData } }) => (
          <div className="flex space-x-2">
            {hasPermission('berth_management_read') && (
              <button
                onClick={() => handleOpenModal(row.original, 'view')}
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`View ${row.original.berth_number}`}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-600" />
              </button>
            )}
            {hasPermission('berth_management_update') && (
              <button
                onClick={() => handleOpenModal(row.original, 'edit')}
                className="btn btn-sm btn-icon btn-light btn-outline"
                aria-label={`Edit ${row.original.berth_number}`}
              >
                <KeenIcon
                  type="filled"
                  icon="pencil"
                  className="text-gray-600"
                />
              </button>
            )}
            {hasPermission('berth_management_delete') && (
              <button
                onClick={() =>
                  setConfirmDeleteState({
                    isOpen: true,
                    berthId: row.original.id as string,
                  })
                }
                className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Delete"
                aria-label={`Delete ${row.original.berth_number}`}
              >
                <KeenIcon type="filled" icon="trash" className="text-red-500" />
              </button>
            )}
            {hasPermission('berth_management_read') && (
              <button
                onClick={() => handleDownloadQrCode(row.original)}
                disabled={downloadingQrId === row.original.id}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Download QR code"
                aria-label={`Download QR code for ${row.original.berth_number}`}
              >
                {downloadingQrId === row.original.id ? (
                  <KeenIcon
                    icon="arrows-circle"
                    className="text-gray-600 animate-spin"
                  />
                ) : (
                  <KeenIcon icon="scan-barcode" className="text-gray-600" />
                )}
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [
      handleOpenModal,
      setConfirmDeleteState,
      hasPermission,
      handleDownloadQrCode,
      downloadingQrId,
    ]
  );

  return (
    <div
      className="berth-listings card card-grid w-full"
      data-testid="berth-modal"
    >
      <div className="px-6 pt-5">
        <h1 className="text-base font-semibold text-gray-900">
          Berth Management
        </h1>
      </div>
      <div className="card-header berth-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="berth-header-left flex items-center gap-2">
          <div className="flex-shrink-0">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-berth"
              aria-label="Search berths by ID"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-berth"
                placeholder="Search berths"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search berths by ID"
              />
            </label>
          </div>
          <div className="flex-shrink-0 w-full sm:w-64">
            <Select
              id="harbor-filter"
              options={harborOptions}
              value={
                harborOptions.find(
                  (option) => option.value === selectedHarbor
                ) || null
              }
              onChange={handleHarborChange}
              isDisabled={
                (!!harborId && location.pathname === '/harbors/details') ||
                isLoading ||
                isFetching ||
                isUpdating ||
                isHarborsLoading
              }
              placeholder={
                isHarborsLoading ? 'Loading harbors...' : 'Select harbor'
              }
              className="react-select-container"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({
                  ...base,
                  borderRadius: '0.5rem',
                  borderColor: '#d1d5db',
                  minHeight: '2.5rem',
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                  boxShadow: 'none',
                  '&:hover': {
                    borderColor: '#93c5fd',
                  },
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 50,
                  marginTop: '0.5rem',
                  borderRadius: '0.375rem',
                  boxShadow:
                    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                }),
                option: (base, state) => ({
                  ...base,
                  fontSize: '0.875rem',
                  backgroundColor: state.isSelected
                    ? '#e0f2fe'
                    : state.isFocused
                      ? '#f3f4f6'
                      : 'white',
                  color: '#1f2937',
                  padding: '0.5rem 0.75rem',
                  cursor: 'pointer',
                }),
                placeholder: (base) => ({
                  ...base,
                  color: '#9ca3af',
                }),
              }}
              aria-label="Filter bookings by harbor"
            />
            {(harborsData?.total_pages || 0) > 1 && (
              <div className="flex justify-between mt-1">
                <button
                  disabled={harborPagination.page === 1}
                  onClick={() =>
                    setHarborPagination((prev) => ({
                      ...prev,
                      page: prev.page - 1,
                    }))
                  }
                  className="btn btn-xs"
                >
                  Previous
                </button>
                <span className="text-xs">
                  Page {harborPagination.page} of{' '}
                  {harborsData?.total_pages || 1}
                </span>
                <button
                  disabled={
                    harborPagination.page >= (harborsData?.total_pages || 1)
                  }
                  onClick={() =>
                    setHarborPagination((prev) => ({
                      ...prev,
                      page: prev.page + 1,
                    }))
                  }
                  className="btn btn-xs"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="berth-header-right flex items-center gap-2">
          <div className="flex items-center gap-2">
            {hasPermission('berth_management_create') && (
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={handleOpenCreateModal}
                aria-label="Add new berth"
              >
                Add New Berth
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {(searchTerm ||
              featuredFilter !== 'all' ||
              selectedBerthIds.length > 0 ||
              (selectedHarbor && !isHarborDetailsRoute)) && (
              <button
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={resetFilters}
                aria-label="Clear all filters"
                title="Clear all filters"
              >
                <KeenIcon
                  type="solid"
                  icon="arrows-circle"
                  className="text-gray-600"
                />
                Clear Filters
              </button>
            )}
            {selectedBerthIds.length > 1 && (
              <button
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={() => setGroupModalOpen(true)}
                aria-label="Create pier"
                title="Create pier"
                data-testid="create-group-button"
              >
                <KeenIcon
                  type="filled"
                  icon="group"
                  className="text-gray-600"
                />
                Make Group
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching || berthFetching ? (
          <div className="text-center py-4" role="status" aria-live="polite">
            Loading berths...
          </div>
        ) : isError ? (
          <div className="text-center py-4 text-red-500" role="alert">
            Failed to load berths. Please try again.
          </div>
        ) : (
          <div className="berth-grid-wrapper">
            <DataGrid
              columns={columns}
              data={filteredData}
              loading={isLoading || isFetching || berthFetching}
              rowSelect={true}
              onRowsSelectChange={(selectedRowIds) =>
                setSelectedBerthIds(Array.from(selectedRowIds))
              }
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems,
                totalPages,
                setPagination,
                info: `{from} - {to} of {count}`,
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'berth_number', desc: false }]}
              serverSide={true}
              onFilterReset={resetFilters}
              hasActiveFilters={
                !!searchTerm ||
                featuredFilter !== 'all' ||
                selectedBerthIds.length > 0 ||
                !!selectedHarbor
              }
              aria-label="Berth listings table"
            />
          </div>
        )}
        <BerthModal
          mode={modalState.mode}
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          setErrors={setErrors}
          onSubmit={handleSubmit}
          isSubmitting={isUpdating || isCreatingGroup}
          open={modalState.isOpen}
          onClose={handleCloseModal}
        />
        <ConfirmationDialog
          open={confirmDeleteState.isOpen}
          onClose={() =>
            setConfirmDeleteState({ isOpen: false, berthId: null })
          }
          onConfirm={handleDelete}
          message="Are you sure you want to delete this berth?"
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="delete-confirmation-title"
          aria-describedby="delete-confirmation-description"
        />
        <ConfirmationDialog
          open={confirmFeaturedState.isOpen}
          onClose={() =>
            setConfirmFeaturedState({
              isOpen: false,
              berthId: null,
              featured: false,
            })
          }
          onConfirm={() =>
            handleToggleFeatured(
              confirmFeaturedState.berthId!,
              confirmFeaturedState.featured
            )
          }
          message={`Are you sure you want to ${confirmFeaturedState.featured ? 'add' : 'remove'} this berth ${confirmFeaturedState.featured ? 'to' : 'from'} featured listings?`}
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="featured-confirmation-title"
          aria-describedby="featured-confirmation-description"
        />
        <Modal
          open={groupModalOpen}
          onClose={() => {
            setGroupModalOpen(false);
            setGroupName('');
            setGroupNameError('');
          }}
          data-testid="group-modal"
        >
          <div
            className="bg-gray-100 p-8 w-full max-w-md mx-auto rounded-xl shadow-2xl flex flex-col"
            data-testid="group-modal-content"
          >
            <h2
              id="group-modal-title"
              className="text-2xl font-semibold text-gray-800 mb-6"
            >
              Create Pier
            </h2>
            <div id="group-modal-description" className="sr-only">
              Form to create a pier
            </div>
            <div className="flex-1">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleCreateGroup();
                }}
                data-testid="group-form"
              >
                <div className="mb-4">
                  <label
                    htmlFor="group-name-input"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Group Name
                  </label>
                  <input
                    aria-label="Group Name"
                    id="group-name-input"
                    type="text"
                    value={groupName}
                    onChange={(e) => {
                      setGroupName(e.target.value);
                      setGroupNameError('');
                    }}
                    className={`input input-bordered w-full h-11 rounded-lg border-gray-300 ${groupNameError ? 'border-red-500' : ''}`}
                    placeholder="Enter pier name"
                    aria-invalid={!!groupNameError}
                    aria-describedby={
                      groupNameError ? 'group-name-error' : undefined
                    }
                    disabled={isCreatingGroup}
                    data-testid="group-name-input"
                  />
                  {groupNameError && (
                    <p
                      id="group-name-error"
                      className="text-red-500 text-xs mt-1"
                      role="alert"
                    >
                      {groupNameError}
                    </p>
                  )}
                </div>
              </form>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setGroupModalOpen(false);
                  setGroupName('');
                  setGroupNameError('');
                }}
                className="btn btn-sm btn-ghost text-gray-600 hover:bg-gray-100 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
                aria-label="Cancel"
                disabled={isCreatingGroup}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateGroup}
                className="btn btn-sm btn-primary text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Create Group"
                disabled={isCreatingGroup}
                data-testid="dialog-submit"
              >
                {isCreatingGroup ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export { BerthListings };
